import axios from '@/plugins/axios'
import type { ApiResponse, Entity, PlanLine, Session, Student, User } from '@/types/global'
import { calculateTotals } from '@/utils/calculateTotalLinesAndPages'
import { toUserFriendlyError } from '@/utils/error-message'
import { isSupportedPresenceEvaluation } from '@/utils/evaluationPresence'
import { filterCurrentStudentSessionEvaluations } from '@/utils/currentSessionEvaluations'
import { context } from '.'

export const fetchSingleSessionStudents = async (
  session: Session,
  fields?: string[],
  planDate?: string
) => {
  const response = await axios.post<ApiResponse<Student[]>>(
    `/api-proxy/${context}/api/1.0/student`,
    {
      jsonrpc: '2.0',
      params: {
        fields,
        session_id: session.id,
        ...(planDate ? { plan_date: planDate } : {}),
        filter: [
          ['id', 'in', session.student_ids.map((s) => s.id)]
          // ['state', '=', 'done']
        ]
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  return response.data.result
}

export const fetchEvaluationStudents = async (
  sessions: Session[],
  fields: string[],
  planDates: Record<number, string> = {}
) => {
  const entries = await Promise.all(
    sessions.map(async (session) => {
      const students = await fetchSingleSessionStudents(session, fields, planDates[session.id])
      return [session.id, students] as const
    })
  )

  return Object.fromEntries(entries) as Record<number, Student[]>
}

export const fetchSessionsStudents = async (sessions: Session[], fields?: string[]) => {
  const result: { [key: number]: Student[] } = {}

  const all = await fetchAllStudents(
    [
      [
        'id',
        'in',
        sessions.reduce(
          (acc: number[], s) => [...acc, ...(s?.student_ids?.map?.((s) => s.id) ?? [])],
          []
        )
      ]
      // ['state', '=', 'done']
    ],
    fields
  )

  for (const session of sessions) {
    result[session.id] = all.filter((s) => session.student_ids.map((s) => s.id).includes(s.id))
  }

  return result
}

export type FetchStudentsParams = {
  filter?: any[]
  group?: string
  page?: number
  limit?: number
  order?: string
  fields?: string[]
}

export type FetchStudentsResult = Student[] & {
  count_total?: number
}

export type StudentLifecycleResult = {
  status?: boolean
  message?: string
}

async function updateStudentLifecycle(
  action: 'delete' | 'archive' | 'unarchive',
  ids: number | number[]
) {
  const response = await axios.post<ApiResponse<StudentLifecycleResult>>(
    `/api-proxy/${context}/api/1.0/student/${action}`,
    {
      jsonrpc: '2.0',
      params: {
        ids: Array.isArray(ids) ? ids : [ids]
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  if (response.data.result?.status === false) {
    throw toUserFriendlyError(response.data.result.message, 'تعذر تنفيذ الإجراء على الطالب')
  }

  return response.data.result
}

export const deleteStudents = (ids: number | number[]) => updateStudentLifecycle('delete', ids)

export const archiveStudents = (ids: number | number[]) => updateStudentLifecycle('archive', ids)

export const unarchiveStudents = (ids: number | number[]) =>
  updateStudentLifecycle('unarchive', ids)

export const fetchAllStudents = async (
  filters: [string, string, any][] = [],
  fields?: string[]
) => {
  const response = await axios.post<ApiResponse<Student[]>>(
    `/api-proxy/${context}/api/1.0/student`,
    {
      jsonrpc: '2.0',
      params: {
        fields,
        filter: [
          ...filters
          // ['state', '=', 'done']
        ]
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  return response.data.result
}

function normalizeStudentsResult(result: any, countTotal?: number): FetchStudentsResult {
  const records = Array.isArray(result)
    ? result
    : (result?.data ?? result?.records ?? result?.items ?? result?.result ?? [])

  const students = (Array.isArray(records) ? records : []) as FetchStudentsResult
  const normalizedCount = result?.count_total ?? result?.total ?? countTotal

  if (typeof normalizedCount === 'number') {
    students.count_total = normalizedCount
  }

  return students
}

export const fetchStudents = async (
  params: FetchStudentsParams = {}
): Promise<FetchStudentsResult> => {
  const { filter = [], group = '', page = 1, limit = 25, order = 'id', fields = [] } = params

  const response = await axios.post<
    ApiResponse<Student[] | { count_total?: number; total?: number }>
  >(`/api-proxy/${context}/api/1.0/student`, {
    jsonrpc: '2.0',
    params: {
      fields,
      filter,
      group,
      page,
      limit,
      order,
      count_total: true
    }
  })

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  // Handle case where API returns 200 but result contains an error
  const result = response.data.result as any
  if (result && typeof result === 'object' && result.status === false && result.message) {
    throw toUserFriendlyError(result.message, 'تعذر تحميل بيانات الطلاب')
  }

  return normalizeStudentsResult(result, (response.data as any).count_total)
}

const studentDetailFields = [
  'name',
  'gender',
  'mobile',
  'mobile_whatsapp_verification',
  'email',
  'nationality_id',
  'country_id',
  'state_id',
  'city',
  'zip',
  'date_of_birth',
  'address',
  'quranic_narratives_id',
  'group_day_id',
  'days_of_recitation_ids',
  'recitation_time',
  'student_parent',
  'student_parent_mobile',
  'student_session_id',
  'current_plan_id',
  'last_memorization',
  'date_start_plan',
  'date_end_plan',
  'daily_recitation_plan_id',
  'recitation_start_id',
  'daily_revision_plan_id',
  'revision_start_id',
  'plan_progress',
  'create_date',
  'state',
  'stage_id'
]

export const fetchStudent = async (id: number) => {
  const response = await axios.post<ApiResponse<Student>>(
    `/api-proxy/${context}/api/1.0/student/${id}`,
    {
      jsonrpc: '2.0',
      params: {
        fields: studentDetailFields,
        filter: [
          // ['state', '=', 'done']
        ]
      }
    }
  )

  return response.data.result
}

export const fetchStudentHandState = async (studentId: number, sessionId: number) => {
  const response = await axios.post<ApiResponse<Array<Pick<Student, 'hand_on'>>>>(
    `/api-proxy/${context}/api/1.0/student`,
    {
      jsonrpc: '2.0',
      params: {
        fields: ['hand_on'],
        filter: [['id', '=', studentId]],
        session_id: sessionId
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  // A student without a persisted hand state is not an error; it simply
  // means their hand is lowered. Never return undefined from a query source.
  return response.data.result?.[0]?.hand_on ?? false
}

export const fetchStudentCallEvent = async (studentId: number, sessionId: number) => {
  const response = await axios.post<ApiResponse<Array<Pick<Student, 'call_event'>>>>(
    `/api-proxy/${context}/api/1.0/student`,
    {
      jsonrpc: '2.0',
      params: {
        fields: ['call_event'],
        filter: [['id', '=', studentId]],
        session_id: sessionId
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  // No call event is the expected idle state. Vue Query requires a concrete
  // result even when the API list is empty.
  return response.data.result?.[0]?.call_event ?? false
}

export type StudentSessionEvent =
  | 'hand_on'
  | 'hand_off'
  | 'call_ring'
  | 'call_started'
  | 'call_paused'
  | 'call_ended'

export type StudentSessionEventResult = {
  student_id: number | false
  session_id: number
  event: StudentSessionEvent
  time: string
}

export const sendStudentSessionEvent = async (
  sessionId: number,
  event: StudentSessionEvent,
  studentId?: number
) => {
  const response = await axios.post<ApiResponse<StudentSessionEventResult>>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/student/event`,
    {
      jsonrpc: '2.0',
      params: {
        session_id: sessionId,
        event,
        ...(studentId ? { student_id: studentId } : {})
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  return response.data.result
}

const getEntityId = (value: number | Entity | undefined) => {
  if (!value) return undefined
  return typeof value === 'number' ? value : value.id
}

const currentStudentFields = [
  'name',
  'email',
  'mobile',
  'student_session_id',
  'current_plan_id',
  'last_memorization',
  'date_start_plan',
  'date_end_plan',
  'stage_id'
]

export const fetchCurrentStudent = async (user: User) => {
  const directStudentId =
    getEntityId(user.student_id) ??
    getEntityId(Array.isArray(user.student_ids) ? user.student_ids[0] : undefined)

  if (directStudentId) {
    const students = await fetchStudents({
      filter: [['id', '=', directStudentId]],
      limit: 1,
      fields: currentStudentFields
    })

    return students[0] ?? null
  }

  const candidates = [
    ['email', '=', user.username],
    ['name', '=', user.partner_display_name],
    ['name', '=', user.name]
  ] as [string, string, any][]

  for (const filter of candidates) {
    const students = await fetchStudents({
      filter: [filter],
      limit: 1,
      fields: currentStudentFields
    })

    if (students[0]) {
      return students[0]
    }
  }

  return null
}

export type StudentForm = {
  id?: number
  first_name: string
  last_name: string
  gender?: 'male' | 'female' | ''
  mobile: string
  email: string
  nationality_id: number
  country_id?: number | false
  state_id?: number
  city: string
  zip: string
  date_of_birth: string
  number_of_parts_saved?: number
  address: string
  quranic_narratives_id?: number | string | false
  group_day_id?: number | false
  recitation_time?: string
  recitation_time_tz?: string
  mosque_id?: number
  student_parent: string
  student_parent_mobile: string
  tmp_password?: string

  // tab 2 data
  student_session_id: number

  date_start_plan: string
  date_end_plan: string

  daily_recitation_plan_id: number | false
  recitation_start_id: number | false

  daily_revision_plan_id: number | false
  revision_start_id: number | false
}

export const createStudent = async (student: Partial<StudentForm>) => {
  const response = await axios.post<ApiResponse<Student | { status: boolean; message: string }>>(
    `/api-proxy/${context}/api/1.0/student/create`,
    {
      jsonrpc: '2.0',
      params: Object.assign(student, {
        date_end_plan:
          !student.date_end_plan || student.date_end_plan === '' ? false : student.date_end_plan,
        date_start_plan:
          !student.date_start_plan || student.date_start_plan === ''
            ? false
            : student.date_start_plan
      })
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error, 'تعذر حفظ بيانات الطالب')
  }

  if (!(response.data.result as { status: boolean; message: string }).status) {
    return Promise.reject(
      toUserFriendlyError(
        (response.data.result as { status: boolean; message: string }).message,
        'تعذر حفظ بيانات الطالب'
      )
    )
  }
  return response.data.result as Student
}

export const getStudentsStatistics = (
  students: { [key: number]: Student[] },
  memorizePlans: PlanLine[][] = [],
  reviewPlans: PlanLine[][] = []
) => {
  if (!students || !Object.keys(students).length)
    return {
      memorizeSum: 0,
      totalStudents: 0,
      allStudents: 0,
      reviewSum: 0,
      totalEvaluations: 0,
      totalMemorize: 0,
      totalReview: 0
    }

  return (Object.keys(students as Object) as string[]).reduce(
    (acc, key) => {
      acc.allStudents += students[Number.parseInt(key)].filter((s) =>
        filterCurrentStudentSessionEvaluations(s, s.evaluations).some(isSupportedPresenceEvaluation)
      ).length

      for (const student of students[Number.parseInt(key)]) {
        acc.totalStudents++

        const completedEvaluations = filterCurrentStudentSessionEvaluations(
          student,
          student.evaluations
        ).filter(
          (evaluation) => evaluation.presence_type === 'present'
        )

        if (!completedEvaluations.length || !student.current_plan_id) {
          continue
        }

        acc.memorizeSum += completedEvaluations.reduce((sum, evaluation) => {
          return sum + Number.parseFloat(evaluation.total_memorize_page as string || '0')
        }, 0)

        acc.reviewSum += completedEvaluations.reduce((sum, evaluation) => {
          return sum + Number.parseFloat(evaluation.total_review_page as string || '0')
        }, 0)
      }

      return acc
    },
    {
      memorizeSum: 0,
      allStudents: 0,

      totalStudents: 0,
      reviewSum: 0,
      totalMemorize: memorizePlans.reduce((acc, p) => {
        return (
          acc +
          p.reduce((acc, l) => {
            return (
              acc +
              calculateTotals(l.surah_from_id.id, l.verse_from, l.surah_to_id.id, l.verse_to)
                .totalPages
            )
          }, 0)
        )
      }, 0),

      totalReview: reviewPlans.reduce((acc, p) => {
        return (
          acc +
          p.reduce((acc, l) => {
            return (
              acc +
              calculateTotals(l.surah_from_id.id, l.verse_from, l.surah_to_id.id, l.verse_to)
                .totalPages
            )
          }, 0)
        )
      }, 0)
    }
  )
}

export const viewStudent = async (id: string) => {
  const response = await axios.post<ApiResponse<Student>>(
    `/api-proxy/${context}/api/1.0/student/view/${id}`,
    {
      jsonrpc: '2.0',
      params: {
        check_token: parseInt(id) * 272333
      }
    }
  )

  return response.data.result
}

export const moveStudents = async (student_ids: number[], session_id: number) => {
  try {
    const response = await axios.post<ApiResponse<Student>>(
      `/api-proxy/${context}/api/1.0/session/student/update`,
      {
        jsonrpc: '2.0',
        params: {
          session_id,
          student_ids
        }
      }
    )

    return response.data.result
  } catch (error) {
    return Promise.reject(toUserFriendlyError(error, 'تعذر نقل الطلاب إلى الحلقة المختارة'))
  }
}

export const sendNextPlan = async (studentId: number) => {
  const response = await axios.post<ApiResponse<Student[]>>(
    `/api-proxy/${context}/api/1.0/send_ayat`,
    {
      jsonrpc: '2.0',
      params: {
        student_id: studentId
      }
    }
  )

  if (response.data.result.error) {
    return Promise.reject(
      toUserFriendlyError(response.data.result.error, 'تعذر إرسال النصاب التالي')
    )
  }

  return response.data.result
}

export type StudentRegistrationForm = {
  first_name: string
  last_name: string
  gender: 'male' | 'female' | ''
  date_of_birth: string
  quranic_narratives_id?: string
  group_day_id?: number
  recitation_time?: string
  recitation_time_tz?: string
  mobile: string
  email: string
  tmp_password: string
}

export const registerStudent = async (student: StudentRegistrationForm) => {
  const response = await axios.post<ApiResponse<{ status: boolean; message: string; id: number }>>(
    `/api-proxy/${context}/api/1.0/register`,
    {
      jsonrpc: '2.0',
      params: student
    }
  )

  if (response.data.error) {
    return Promise.reject(toUserFriendlyError(response.data.error))
  }

  if (!response.data.result.status) {
    return Promise.reject(toUserFriendlyError(response.data.result.message, 'تعذر تسجيل الطالب'))
  }

  return response.data.result
}

export const acceptStudent = async (studentIds: number | number[]) => {
  const response = await axios.post<ApiResponse<any>>(
    `/api-proxy/${context}/api/1.0/student/accept`,
    {
      jsonrpc: '2.0',
      params: {
        student_ids: Array.isArray(studentIds) ? studentIds : [studentIds]
      }
    }
  )

  if (response.data.error) {
    return Promise.reject(toUserFriendlyError(response.data.error))
  }

  return response.data.result
}

export const refuseStudent = async (studentIds: number | number[], refuseReason: string) => {
  const response = await axios.post<ApiResponse<any>>(
    `/api-proxy/${context}/api/1.0/student/refuse`,
    {
      jsonrpc: '2.0',
      params: {
        student_ids: Array.isArray(studentIds) ? studentIds : [studentIds],
        refuse_reason: refuseReason
      }
    }
  )

  if (response.data.error) {
    return Promise.reject(toUserFriendlyError(response.data.error))
  }

  return response.data.result
}

export type RegistrationStatusResponse = {
  name: string
  civil_registry: string | false
  mobile: string
  email: string
  state: 'draft' | 'in_progress' | 'done' | 'cancel'
  stage_id:
    | {
        id: number
        name: string
        display_name?: string
        code?: string
      }
    | false
}

export const checkRegistrationStatus = async (mobile: string, civil_registry: string) => {
  const response = await axios.post<ApiResponse<RegistrationStatusResponse>>(
    `/api-proxy/${context}/api/1.0/register/search`,
    {
      jsonrpc: '2.0',
      params: {
        mobile,
        civil_registry
      }
    }
  )

  if (response.data.error) {
    return Promise.reject(toUserFriendlyError(response.data.error))
  }

  return response.data.result
}

export const resetStudentPassword = async (studentId: number, password: string) => {
  const response = await axios.post<ApiResponse<{ status: boolean; message: string }>>(
    `/api-proxy/${context}/api/1.0/student/${studentId}/reset_password`,
    {
      jsonrpc: '2.0',
      params: {
        password
      }
    }
  )

  if (response.data.error) {
    return Promise.reject(toUserFriendlyError(response.data.error))
  }

  const result = response.data.result
  if (result && typeof result === 'object' && result.status === false && result.message) {
    return Promise.reject(toUserFriendlyError(result.message, 'تعذر إعادة تعيين كلمة المرور'))
  }

  return response.data.result
}
